import { uniqueId } from 'lodash'
import { SyntheticEvent, useContext, useState } from 'react'
import { giphyLightestGrey, giphyDarkCharcoal } from '@giphy/js-brand'
import DotsLoader from 'shared/components/loaders/dots'
import styled from 'styled-components'
import { CAPTION_FILE_EXTENSIONS } from '../../../constants'
import CreationToolSuperCutField from '../../../types/super-cut-field'
import { FileButton } from '../../buttons'
import { CreationCaptureInfoContext } from '../../supercut/shared/components/capture-info/manager'
import API from '../../supercut/shared/utils/api'
import FieldTitle from './field-title'
import { Button } from 'shared/components/buttons'
import * as colors from '@giphy/js-brand'
import UserContext from 'shared/contexts/user'
import SuperCutSegmentationType from '../../../types/super-cut-segmentation-type'
import { googleAnalyticsEvent } from 'shared/util/analytics'

const api = new API()

const Container = styled.div`
    grid-column: span 2;
`

const CaptionFileButton = styled(FileButton)`
    height: 36px;
    background: ${giphyDarkCharcoal};
    color: ${giphyLightestGrey};
    align-items: center;
    padding-left: 15px;
    display: flex;
`

const FilenameDisplayText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
`

const RemoveButton = styled(Button)`
    background: ${colors.primaryCTA};
    flex-grow: 0;
    flex-shrink: 0;
    line-height: 40px;
    padding: 0;
    width: 34px;
    position: relative;
`

type Props = {
    field: CreationToolSuperCutField
}

const CreationCaptureCaptionFileField = ({ field }: Props) => {
    const { caption, setCaption, hasAutoCaption, setSegmentationType, setAutoCaption } =
        useContext(CreationCaptureInfoContext)
    const { user } = useContext(UserContext)

    const [isLoading, setIsLoading] = useState(false)

    const displayText = hasAutoCaption ? 'Imported: YouTube' : caption ? caption.title : 'Upload Subtitle Track'

    const onFileInput = async (e: SyntheticEvent<HTMLInputElement, Event>) => {
        const { files } = e.currentTarget
        if (files) {
            setIsLoading(true)
            try {
                const fileExtension = files[0].name.split('.').pop()
                const captionFileUrl = await api.uploadCaptureAsset(files[0], fileExtension!)
                const newCaption = { title: files[0].name, url: captionFileUrl, id: uniqueId('caption') }
                googleAnalyticsEvent({ category: 'Super Cut', action: 'Transcription Upload', label: user.id })
                setCaption(newCaption)
                setSegmentationType(SuperCutSegmentationType.Caption)
                setIsLoading(false)
            } catch (e) {}
        }
    }

    const clearCaptionField = () => {
        setCaption(undefined)
        setAutoCaption(false)
        setSegmentationType(SuperCutSegmentationType.Scene)
    }

    return (
        <Container>
            {isLoading ? (
                <DotsLoader />
            ) : (
                <>
                    <FieldTitle field={field} />
                    <CaptionFileButton>
                        <FilenameDisplayText>{displayText}</FilenameDisplayText>
                        <input
                            type="file"
                            disabled={hasAutoCaption ? true : undefined}
                            accept={CAPTION_FILE_EXTENSIONS.join(',')}
                            onInput={onFileInput}
                        />
                        <RemoveButton style={{ display: caption ? '' : 'none' }} onClick={clearCaptionField}>
                            <i className="ss-hyphen" />
                        </RemoveButton>
                    </CaptionFileButton>
                </>
            )}
        </Container>
    )
}

export default CreationCaptureCaptionFileField
